import { useEffect, useState } from "react";
import { Routes, Route, NavLink, Link, useNavigate } from "react-router-dom";
import { api } from "./api";
import {
  LayoutDashboard, CalendarDays, Users, GraduationCap, Camera, Megaphone,
  Home, Type, Settings as SettingsIcon, LogOut, Menu, X, ExternalLink, Lock, School, Youtube, Trophy,
} from "lucide-react";
import Dashboard from "./Dashboard";
import GamesSection from "./GamesSection";
import PlayersSection from "./PlayersSection";
import CoachesSection from "./CoachesSection";
import CollegeSection from "./CollegeSection";
import RecordsSection from "./RecordsSection";
import VideosSection from "./VideosSection";
import PhotosSection from "./PhotosSection";
import NewsSection from "./NewsSection";
import TextSection from "./TextSection";
import HomepageSection from "./HomepageSection";
import SettingsSection from "./SettingsSection";

const SECTIONS = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", testid: "admin-nav-dashboard" },
  { to: "/admin/games", icon: CalendarDays, label: "Games & Schedule", testid: "admin-nav-games" },
  { to: "/admin/players", icon: Users, label: "Players", testid: "admin-nav-players" },
  { to: "/admin/coaches", icon: GraduationCap, label: "Coaches & Staff", testid: "admin-nav-coaches" },
  { to: "/admin/college", icon: School, label: "Bears in College", testid: "admin-nav-college" },
  { to: "/admin/records", icon: Trophy, label: "Records & Awards", testid: "admin-nav-records" },
  { to: "/admin/photos", icon: Camera, label: "Photos", testid: "admin-nav-photos" },
  { to: "/admin/videos", icon: Youtube, label: "Videos", testid: "admin-nav-videos" },
  { to: "/admin/news", icon: Megaphone, label: "News & Announcements", testid: "admin-nav-news" },
  { to: "/admin/homepage", icon: Home, label: "Homepage", testid: "admin-nav-homepage" },
  { to: "/admin/text", icon: Type, label: "Website Text", testid: "admin-nav-text" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Website Settings", testid: "admin-nav-settings" },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-bold uppercase tracking-wide transition-colors ${
    isActive ? "bg-[#0F4C2A] text-white shadow-md" : "text-slate-600 hover:bg-slate-100"
  }`;

const loginInputCls =
  "w-full px-4 py-3.5 rounded-md border border-slate-300 bg-white text-base focus:outline-none focus:ring-2 focus:ring-[#0F4C2A]/40 focus:border-[#0F4C2A]";

export default function AdminApp() {
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const r = await api.post("/auth/login", loginForm);
      setUser(r.data);
      setLoginForm({ username: "", password: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Invalid username or password.");
    }
    setLoggingIn(false);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // cookie cleared server-side best effort
    }
    setUser(null);
    navigate("/admin", { replace: true });
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="admin-loading">
        <p className="text-slate-500 font-semibold">Loading admin...</p>
      </div>
    );
  }

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A3823] diagonal-stripes px-4" data-testid="admin-login-screen">
        <div className="bg-white rounded-xl shadow-2xl p-8 sm:p-10 max-w-md w-full">
          <div className="text-center">
            <img src="/assets/bears-logo.jpeg" alt="Bears logo" className="h-20 w-20 rounded-full object-cover mx-auto ring-4 ring-[#0F4C2A]/15" />
            <h1 className="font-condensed text-3xl font-black uppercase text-[#0F4C2A] mt-5">Bears Baseball Admin</h1>
            <p className="text-slate-600 text-sm mt-2">Sign in to manage the website.</p>
          </div>
          {loginError && (
            <p className="mt-5 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3" data-testid="admin-login-error">
              {loginError}
            </p>
          )}
          <form onSubmit={submitLogin} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5" htmlFor="admin-username">Username</label>
              <input
                id="admin-username"
                autoComplete="username"
                required
                className={loginInputCls}
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                data-testid="admin-login-username"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5" htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                autoComplete="current-password"
                type="password"
                required
                className={loginInputCls}
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                data-testid="admin-login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-button"
              className="w-full inline-flex items-center justify-center gap-3 bg-[#0F4C2A] hover:bg-[#0A3823] disabled:opacity-60 text-white px-6 py-4 rounded-md font-bold text-base uppercase tracking-wide transition-colors shadow-lg"
            >
              <Lock size={18} /> {loggingIn ? "Signing in..." : "Login"}
            </button>
          </form>
          <div className="text-center">
            <Link to="/" className="inline-block mt-5 text-sm text-slate-500 hover:text-[#0F4C2A] font-semibold" data-testid="admin-login-back-link">
              Back to the website
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const sidebar = (
    <nav className="flex flex-col gap-1" data-testid="admin-sidebar">
      {SECTIONS.map((s) => (
        <NavLink key={s.to} to={s.to} end={s.end} className={linkCls} data-testid={s.testid} onClick={() => setMenuOpen(false)}>
          <s.icon size={18} /> {s.label}
        </NavLink>
      ))}
      <div className="border-t border-slate-200 mt-4 pt-4 flex flex-col gap-1">
        <Link to="/" className="flex items-center gap-3 px-4 py-3 rounded-md text-sm font-bold uppercase tracking-wide text-slate-600 hover:bg-slate-100" data-testid="admin-nav-view-site">
          <ExternalLink size={18} /> View Website
        </Link>
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 rounded-md text-sm font-bold uppercase tracking-wide text-red-700 hover:bg-red-50 text-left" data-testid="admin-logout-button">
          <LogOut size={18} /> Log Out
        </button>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-dashboard">
      <aside className="hidden lg:flex flex-col w-72 shrink-0 bg-white border-r border-slate-200 p-5 sticky top-0 h-screen overflow-y-auto">
        <div className="flex items-center gap-3 px-2 pb-5 border-b border-slate-200 mb-5">
          <img src="/assets/bears-logo.jpeg" alt="Bears logo" className="h-11 w-11 rounded-full object-cover ring-2 ring-[#0F4C2A]/15" />
          <div className="leading-tight">
            <span className="font-condensed block text-lg font-extrabold uppercase text-[#0F4C2A]">Bears Admin</span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-[#D97706]">Website Control Panel</span>
          </div>
        </div>
        {sidebar}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-2.5">
            <img src="/assets/bears-logo.jpeg" alt="Bears logo" className="h-9 w-9 rounded-full object-cover" />
            <span className="font-condensed text-lg font-extrabold uppercase text-[#0F4C2A]">Bears Admin</span>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 text-[#0F4C2A]" aria-label="Menu" data-testid="admin-mobile-menu-button">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {menuOpen && (
          <div className="lg:hidden bg-white border-b border-slate-200 p-4" data-testid="admin-mobile-nav">
            {sidebar}
          </div>
        )}

        <div className="hidden lg:flex items-center justify-end gap-3 px-8 py-4 bg-white border-b border-slate-200">
          <span className="text-sm font-semibold text-slate-700" data-testid="admin-user-name">Signed in as {user.name || user.username}</span>
        </div>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-6xl w-full mx-auto">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="games" element={<GamesSection />} />
            <Route path="players" element={<PlayersSection />} />
            <Route path="coaches" element={<CoachesSection />} />
            <Route path="college" element={<CollegeSection />} />
            <Route path="records" element={<RecordsSection />} />
            <Route path="videos" element={<VideosSection />} />
            <Route path="photos" element={<PhotosSection />} />
            <Route path="news" element={<NewsSection />} />
            <Route path="homepage" element={<HomepageSection />} />
            <Route path="text" element={<TextSection />} />
            <Route path="settings" element={<SettingsSection />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
